import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Instagram, Facebook, Youtube, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/lib/cart";

export const INSTAGRAM_URL = "https://instagram.com/poolsidecooking";
export const TIKTOK_URL = "https://tiktok.com/@poolsidecooking";
export const FACEBOOK_URL = "https://facebook.com/poolsidecooking";
export const YOUTUBE_URL = "https://www.youtube.com/@PoolsideCooking";
export const X_URL = "https://x.com/poolsidecooking";

export function XIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117l9.966 15.644z" />
    </svg>
  );
}

export function TikTokIcon({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}

export const NAV = [
  { to: "/", label: "Home", testId: "nav-link-home" },
  { to: "/watch", label: "Watch", testId: "nav-link-watch" },
  { to: "/spotlight", label: "Celebrity Guests", testId: "nav-link-spotlight" },
  { to: "/cafe", label: "Café", testId: "nav-link-cafe" },
  { to: "/events", label: "Events", testId: "nav-link-events" },
  { to: "/shop", label: "Shop", testId: "nav-link-shop" },
  { to: "/about", label: "About", testId: "nav-link-about" },
];

export function Wordmark({ dark = false }) {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
      <img src="/logo.png" alt="Poolside Cooking" className="h-11 sm:h-14 w-auto" />
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const { count } = useCart();

  const cartLink = (
    <Link to="/cart" aria-label="Shopping bag" className="relative text-slate-500 hover:text-pool transition-colors" data-testid="nav-cart">
      <ShoppingBag className="h-4 w-4" />
      {count > 0 && (
        <span className="absolute -top-2 -right-2 bg-pool text-white text-[9px] font-bold h-4 min-w-4 px-1 rounded-full flex items-center justify-center" data-testid="nav-cart-count">
          {count}
        </span>
      )}
    </Link>
  );

  const linkClass = ({ isActive }) =>
    `text-[11px] tracking-[0.18em] uppercase font-semibold transition-colors ${
      isActive ? "text-pool" : "text-slate-600 hover:text-slate-950"
    }`;

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-100">
      <div className="container-site flex items-center justify-between h-16 sm:h-20 gap-4">
        <Wordmark />

        <nav className="hidden xl:flex items-center gap-7" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkClass} data-testid={item.testId}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-6">
          {cartLink}
          <div className="flex items-center gap-4 border-l border-slate-200 pl-6" data-testid="header-socials">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram @poolsidecookingshow"
              className="text-slate-400 hover:text-pool transition-all duration-200 hover:-translate-y-0.5"
              data-testid="social-instagram"
            >
              <Instagram className="h-[18px] w-[18px]" />
            </a>
            <a
              href={TIKTOK_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="TikTok @poolsidecooking"
              className="text-slate-400 hover:text-pool transition-all duration-200 hover:-translate-y-0.5"
              data-testid="social-tiktok"
            >
              <TikTokIcon className="h-[17px] w-[17px]" />
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook Poolside Cooking"
              className="text-slate-400 hover:text-pool transition-all duration-200 hover:-translate-y-0.5"
              data-testid="social-facebook"
            >
              <Facebook className="h-[18px] w-[18px]" />
            </a>
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="YouTube Poolside Cooking"
              className="text-slate-400 hover:text-pool transition-all duration-200 hover:-translate-y-0.5"
              data-testid="social-youtube"
            >
              <Youtube className="h-5 w-5" />
            </a>
            <a
              href={X_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="X Poolside Cooking"
              className="text-slate-400 hover:text-pool transition-all duration-200 hover:-translate-y-0.5"
              data-testid="social-x"
            >
              <XIcon className="h-[16px] w-[16px]" />
            </a>
          </div>
          <Link
            to="/be-on-the-show"
            data-testid="be-on-show-button"
            className="inline-flex items-center gap-2 bg-pool hover:bg-pool-dark text-white text-[11px] tracking-[0.15em] uppercase font-bold px-5 py-3 rounded-full transition-all duration-200 hover:-translate-y-0.5"
          >
            Be on the Show <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="xl:hidden flex items-center gap-4">
          {cartLink}
          <button
            className="text-slate-900 p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="xl:hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl" data-testid="mobile-nav">
          <div className="container-site py-6 flex flex-col gap-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-sm tracking-[0.18em] uppercase font-semibold transition-colors ${
                    isActive ? "text-pool" : "text-slate-600"
                  }`
                }
                data-testid={`mobile-${item.testId}`}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-6 pt-1" data-testid="mobile-socials">
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-slate-400 hover:text-pool transition-colors" data-testid="mobile-social-instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href={TIKTOK_URL} target="_blank" rel="noreferrer" aria-label="TikTok" className="text-slate-400 hover:text-pool transition-colors" data-testid="mobile-social-tiktok">
                <TikTokIcon className="h-5 w-5" />
              </a>
              <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-slate-400 hover:text-pool transition-colors" data-testid="mobile-social-facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href={YOUTUBE_URL} target="_blank" rel="noreferrer" aria-label="YouTube" className="text-slate-400 hover:text-pool transition-colors" data-testid="mobile-social-youtube">
                <Youtube className="h-[22px] w-[22px]" />
              </a>
              <a href={X_URL} target="_blank" rel="noreferrer" aria-label="X" className="text-slate-400 hover:text-pool transition-colors" data-testid="mobile-social-x">
                <XIcon className="h-[18px] w-[18px]" />
              </a>
            </div>
            <Link
              to="/be-on-the-show"
              onClick={() => setOpen(false)}
              data-testid="mobile-be-on-show-button"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-pool text-white text-xs tracking-[0.15em] uppercase font-bold px-6 py-4 rounded-full"
            >
              Be on the Show <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
